import { validatePolicyNumber } from "service/policy/validatePolicyNumber";
import {
  isAllowedToMakeCCPayment,
  isInterestSensitiveWholeLifePolicy,
  isPolicyInactiveByPolicyStatus,
} from "js/utilities";
import { useTranslation } from "react-i18next";
import { v4 as uuidv4 } from "uuid";
import { useState } from "react";

import EverlakeLoadingOverlay from "components/General/EverlakeLoadingOverlay";
import FormInputError from "components/General/FormInputError";
import moment from "moment/moment";
import { isAnnuityPolicy } from "js/utilities/isAnnuityPolicy";
export default function PaymentPolicy({
  register,
  errors,
  setCurrentStep,
  getValues,
  setPolicy,
  setError,
  clearErrors,
  isNonNaturalEntity,
  setIsNonNaturalEntity,
}) {
  const { t } = useTranslation();

  const UUID = uuidv4();
  const [isLoading, setIsLoading] = useState(false);
  const [inValidInsuredInput, setInValidInsuredInput] = useState();
  const validateField = (fieldName, errorMessage) => {
    if (!getValues(fieldName)) {
      setError(fieldName, {
        message: errorMessage,
      });
      return false;
    } else {
      clearErrors(fieldName);
      return true;
    }
  };

  // make sure all payor require values has input before enabling submit
  const validatePayor = async () => {
    if (
      validateField(
        "policyNumber",
        t("UnauthMakeAPayment-paymentpolicy-policy-number-required")
      ) &&
      (isNonNaturalEntity ||
        (validateField(
          "insuredFirstName",
          t("UnauthMakeAPayment-paymentpolicy-firstname-required")
        ) &&
          validateField(
            "insuredLastName",
            t("UnauthMakeAPayment-paymentpolicy-lastname-required")
          ) &&
          validateField(
            "insuredZipcode",
            t("UnauthMakeAPayment-paymentpolicy-zipcode-required")
          ) &&
          validateField(
            "insuredDob",
            t("UnauthMakeAPayment-paymentpolicy-dob-required")
          )))
    ) {
      let queryString = `ContractId=${getValues("policyNumber")
        .toUpperCase()
        .trim()}&ValidateInsured=${!isNonNaturalEntity}&FirstName=${getValues(
        "insuredFirstName"
      )}&LastName=${getValues("insuredLastName")}&DOB=${moment(
        getValues("insuredDob")
      ).format("MM/DD/YYYY")}&PostalCode=${getValues("insuredZipcode")}`;
      setIsLoading(true);
      await validatePolicyNumber(queryString)
        .then((response) => {
          if (response?.contractId) {
            setPolicy(response);
            if (
              isPolicyInactiveByPolicyStatus(response?.status) ||
              isAnnuityPolicy(response) ||
              isInterestSensitiveWholeLifePolicy(response)
            ) {
              setCurrentStep(1);
              setInValidInsuredInput(
                t("UnauthMakeAPayment-paymentpolicy-invalid-policy")
              );
            } else if (
              ["LZ", "LQ", "LOP"].includes(
                response?.product?.type?.toUpperCase()
              )
            ) {
              setCurrentStep(1);
              setInValidInsuredInput(
                t("UnauthMakeAPayment-paymentpolicy-invalid-msg1")
              );
            } else if (isAllowedToMakeCCPayment(response, "onetimepayment")) {
              setInValidInsuredInput(undefined);
              setCurrentStep(2);
            } else {
              setCurrentStep(1);
              setInValidInsuredInput(
                t("UnauthMakeAPayment-paymentpolicy-invalid-input")
              );
            }
          } else {
            setCurrentStep(1);
            setInValidInsuredInput(
              t("UnauthMakeAPayment-paymentpolicy-invalid-policy")
            );
          }
          setIsLoading(false);
        })
        .catch((err) => {
          console.error(
            "Server refused to connect, please try again later.",
            err
          );
          setCurrentStep(1);
          setIsLoading(false);
          setInValidInsuredInput(
            t("UnauthMakeAPayment-paymentpolicy-invalid-policy")
          );
        });
    }
  };

  const handlePolicyBlur = async () => {
    const policyNumber = getValues("policyNumber");
    if (!policyNumber) return;
    const queryString = `ContractId=${policyNumber
      .toUpperCase()
      .trim()}&ValidateInsured=false`;

    try {
      setIsLoading(true);
      const response = await validatePolicyNumber(queryString);

      if (response?.isNonNaturalEntityPolicy === true) {
        setIsNonNaturalEntity(true);
        clearErrors([
          "insuredFirstName",
          "insuredLastName",
          "insuredZipcode",
          "insuredDob",
        ]);
      } else {
        setIsNonNaturalEntity(false);
      }
    } catch (e) {
      console.error("Policy Validation Failed", e);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <EverlakeLoadingOverlay
      isLoading={isLoading}
      loadingText={t("UnauthMakeAPayment-paymentpolicy-loading-text")}
    >
      <div className="ui-form-card-no-event">
        <label id={UUID + "-label"} className="block mb-1">
          {t("UnauthMakeAPayment-paymentpolicy-contract-number")}
        </label>
        <input
          id={UUID}
          className="uppercase placeholder:normal-case"
          aria-labelledby={UUID + "-label"}
          aria-describedby={`${UUID + "-help"} ${UUID + "-error"}`}
          type="text"
          placeholder="Please enter a Contract Number"
          {...register("policyNumber", {
            required: true,
            onBlur: handlePolicyBlur,
          })}
        />
        <div id={UUID + "-error"}>
          {errors.policyNumber?.message && (
            <FormInputError errorMessage={errors.policyNumber.message} />
          )}
        </div>
        {inValidInsuredInput && (
          <div id={UUID + "-PolicyNotFound"}>
            <FormInputError errorMessage={inValidInsuredInput} />
          </div>
        )}
        <div
          id={UUID + "-help"}
          className="text-xxs text-theme-text-tertiary mt-1"
        >
          {t("UnauthMakeAPayment-paymentpolicy-subtitle")}
        </div>
        <div className="ui-form-card-no-event m-0 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-2 gap-4 pt-3">
          <div>
            <label htmlFor={UUID + "-insuredFirstName"} className="block mb-1">
              {isNonNaturalEntity
                ? t(
                    "UnauthMakeAPayment-paymentpolicy-firstname-withoutAsterisk"
                  )
                : t("UnauthMakeAPayment-paymentpolicy-firstname")}
            </label>
            <input
              id={UUID + "-insuredFirstName"}
              aria-describedby={UUID + "-insuredFirstNameerror"}
              type="text"
              placeholder={t(
                "UnauthMakeAPayment-paymentpolicy-placeholder-firstname"
              )}
              {...register("insuredFirstName", {
                required: !isNonNaturalEntity,
              })}
            />
            <div id={UUID + "-insuredFirstNameerror"}>
              {errors.insuredFirstName?.message && (
                <FormInputError
                  errorMessage={errors.insuredFirstName.message}
                />
              )}
            </div>
          </div>
          <div>
            <label htmlFor={UUID + "-insuredLastName"} className="block mb-1">
              {isNonNaturalEntity
                ? t("UnauthMakeAPayment-paymentpolicy-lastname-withoutAsterisk")
                : t("UnauthMakeAPayment-paymentpolicy-lastname")}
            </label>
            <input
              id={UUID + "-insuredLastName"}
              aria-describedby={UUID + "-insuredLastNameerror"}
              type="text"
              placeholder={t(
                "UnauthMakeAPayment-paymentpolicy-placeholder-lastname"
              )}
              {...register("insuredLastName", {
                required: !isNonNaturalEntity,
              })}
            />
            <div id={UUID + "-insuredLastNameerror"}>
              {errors.insuredLastName?.message && (
                <FormInputError errorMessage={errors.insuredLastName.message} />
              )}
            </div>
          </div>
          <div>
            <label htmlFor={UUID + "-insuredZipcode"} className="block mb-1">
              {isNonNaturalEntity
                ? t("UnauthMakeAPayment-paymentpolicy-zipcode-withoutAsterisk")
                : t("UnauthMakeAPayment-paymentpolicy-zipcode")}
            </label>
            <input
              id={UUID + "-insuredZipcode"}
              aria-describedby={UUID + "-insuredZipcodeerror"}
              type="text"
              placeholder={t(
                "UnauthMakeAPayment-paymentpolicy-placeholder-zipcode"
              )}
              {...register("insuredZipcode", {
                required: !isNonNaturalEntity,
              })}
            />
            <div id={UUID + "-insuredZipcodeerror"}>
              {errors.insuredZipcode?.message && (
                <FormInputError errorMessage={errors.insuredZipcode.message} />
              )}
            </div>
          </div>
          <div>
            <label htmlFor={`${UUID}-insuredDobdob`} className="block mb-1">
              {isNonNaturalEntity
                ? t("UnauthMakeAPayment-paymentpolicy-dob-withoutAsterisk")
                : t("UnauthMakeAPayment-paymentpolicy-dob")}
            </label>
            <input
              id={`${UUID}-insuredDobdob`}
              type="date"
              {...register("insuredDob", {
                required: !isNonNaturalEntity,
              })}
            />
            <FormInputError
              id={`${UUID}-insuredDob-error`}
              errorMessage={errors.insuredDob?.message}
            />
          </div>
        </div>
        <div className="mt-4 text-right">
          <button
            id="ID_registrationStep1Btn"
            className="ui-btn-primary ui-btn-min-w mt-2"
            aria-expanded="false"
            type="button"
            onClick={() => validatePayor()}
          >
            {t("UnauthMakeAPayment-paymentpolicy-continue")}
          </button>
        </div>
      </div>
    </EverlakeLoadingOverlay>
  );
}
